import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { ArrowLeft, Download, Send, CheckCircle2, RefreshCcw } from "lucide-react";

const TYPE_FILTERS = [
  ["all", "All Participants"], ["prime", "Prime Suppliers"], ["sbe", "SBEs"],
  ["tl_attendee", "T+L Team"], ["summit_guest", "Summit Guests"], ["speaker", "Speakers"],
];
const OPP_LABELS = { yes: "Yes", possibly: "Possibly / Follow-up", not_yet: "Not Yet", no: "No" };
const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York" }) + " ET" : "—";

function Stat({ label, value, sub, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">{label}</div>
      <div className="serif text-4xl mt-2">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminSurvey() {
  const [survey, setSurvey] = useState(null);
  const [recipientCount, setRecipientCount] = useState(0);
  const [results, setResults] = useState(null);
  const [responses, setResponses] = useState([]);
  const [themes, setThemes] = useState([]);
  const [audience, setAudience] = useState("all");
  const [mmOnly, setMmOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    const { data: list } = await api.get("/admin/surveys");
    const s = list.surveys[0];
    if (!s) return;
    const [{ data: det }, { data: resp }] = await Promise.all([
      api.get(`/admin/surveys/${s.id}`),
      api.get(`/admin/surveys/${s.id}/responses`),
    ]);
    setSurvey(det.survey); setRecipientCount(det.recipient_count); setThemes(det.themes);
    setResponses(resp.responses);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!survey) return;
    const p = new URLSearchParams();
    if (audience !== "all") p.set("audience", audience);
    if (mmOnly) p.set("matchmaking_only", "true");
    api.get(`/admin/surveys/${survey.id}/results?${p}`).then(({ data }) => setResults(data));
  }, [survey, audience, mmOnly]);

  const act = async (fn, label) => {
    setBusy(true);
    try { await fn(); toast.success(label); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const approve = () => act(() => api.post(`/admin/surveys/${survey.id}/approve`), "Survey approved");
  const sendNow = async () => {
    const confirmResend = !!survey.sent_at;
    if (confirmResend && !window.confirm("The launch email was already sent. Resend to ALL recipients?")) return;
    await act(async () => {
      const { data } = await api.post(`/admin/surveys/${survey.id}/send`, { confirm_resend: confirmResend });
      if (data.already_sent) throw new Error(data.message);
    }, "Launch email sent");
  };
  const sendReminder = async () => {
    const confirmResend = !!survey.reminder_sent_at;
    if (confirmResend && !window.confirm("Reminder already sent. Resend to non-responders again?")) return;
    await act(() => api.post(`/admin/surveys/${survey.id}/send-reminder`, { confirm_resend: confirmResend }), "Reminder sent to non-responders");
  };

  const setTheme = async (rid, theme) => {
    try { await api.patch(`/admin/surveys/${survey.id}/responses/${rid}`, { comment_theme: theme }); toast.success("Theme saved"); load(); }
    catch { toast.error("Failed"); }
  };
  const reopen = async (rid) => {
    if (!window.confirm("Reopen (delete) this response so the participant can submit again?")) return;
    try { await api.patch(`/admin/surveys/${survey.id}/responses/${rid}`, { reopen: true }); toast.success("Response reopened"); load(); }
    catch { toast.error("Failed"); }
  };
  const exportXlsx = async () => {
    const res = await api.get(`/admin/surveys/${survey.id}/export.xlsx`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "summit-survey-results.xlsx";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const pct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

  if (!survey) return <Layout><div className="max-w-6xl mx-auto px-6 py-20 text-zinc-500">Loading survey…</div></Layout>;

  const launched = !!survey.approved_at && new Date(survey.launch_at) <= new Date();
  const status = !survey.approved_at ? "Draft — awaiting approval"
    : survey.sent_at ? "Sent" : (launched ? "Live (email pending)" : "Approved — scheduled");

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-survey">
        <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500 hover:text-black"><ArrowLeft size={14} /> Admin</Link>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-2">
          <div>
            <h1 className="serif text-3xl sm:text-4xl">Post-Event Survey</h1>
            <p className="text-sm text-zinc-500 mt-1">{survey.title}</p>
          </div>
          <button onClick={exportXlsx} className="btn-outline-black h-10 px-4 text-xs inline-flex items-center gap-2" data-testid="survey-export"><Download size={14} /> Excel</button>
        </div>

        {/* Send control panel */}
        <div className="card-flat p-5 mt-6 grid md:grid-cols-2 gap-6" data-testid="survey-controls">
          <div className="space-y-1 text-sm">
            <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-2">Distribution status</div>
            <div>Status: <strong data-testid="survey-status">{status}</strong></div>
            <div>Scheduled send: <strong>{fmt(survey.launch_at)}</strong></div>
            <div>Launch sent: <strong>{survey.sent_at ? fmt(survey.sent_at) : "Not sent"}</strong>
              {survey.sent_stats && <span className="text-zinc-500"> · {survey.sent_stats.recipients} recipients · {survey.sent_stats.success} ok · {survey.sent_stats.failed} failed</span>}</div>
            <div>Reminder ({fmt(survey.reminder_at)}): <strong>{survey.reminder_sent_at ? fmt(survey.reminder_sent_at) : "Not sent"}</strong>
              {survey.reminder_stats && <span className="text-zinc-500"> · {survey.reminder_stats.recipients} sent</span>}</div>
            <div className="text-zinc-500 pt-1">Eligible recipients: {recipientCount} (DA Staff excluded)</div>
          </div>
          <div className="flex flex-col gap-2 justify-center">
            {!survey.approved_at && (
              <button onClick={approve} disabled={busy} className="btn-black h-11 text-xs inline-flex items-center justify-center gap-2" data-testid="survey-approve"><CheckCircle2 size={15} /> Preview approved — Approve for launch</button>
            )}
            {survey.approved_at && (
              <>
                <button onClick={sendNow} disabled={busy} className="btn-black h-11 text-xs inline-flex items-center justify-center gap-2" data-testid="survey-send-now"><Send size={15} /> {survey.sent_at ? "Resend launch email" : "Send Now (optional — skip the wait)"}</button>
                <button onClick={sendReminder} disabled={busy} className="btn-outline-black h-11 text-xs inline-flex items-center justify-center gap-2" data-testid="survey-send-reminder"><RefreshCcw size={14} /> Send reminder to non-responders</button>
              </>
            )}
            <Link to="/survey?preview=1" target="_blank" className="btn-outline-black h-11 text-xs inline-flex items-center justify-center" data-testid="survey-preview">Preview participant view</Link>
          </div>
        </div>

        {survey.approved_at && !survey.sent_at && (
          <div className="card-flat p-4 mt-3 flex items-start gap-3 bg-emerald-50 border-emerald-200" data-testid="survey-auto-armed">
            <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <strong>Auto-send armed — nothing else to do.</strong> The launch email will go out on its own at <strong>{fmt(survey.launch_at)}</strong>, then a reminder to non-responders at <strong>{fmt(survey.reminder_at)}</strong>. You do <em>not</em> need to be online. The “Send Now” button is only if you want to launch earlier.
            </div>
          </div>
        )}

        <div className="flex gap-2 border-b border-zinc-200 mt-8 mb-6">
          {[["overview", "Analytics"], ["responses", "Responses & Themes"]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} className={`px-4 py-2 text-xs uppercase tracking-[0.16em] border-b-2 -mb-px ${tab === v ? "border-black text-black" : "border-transparent text-zinc-400 hover:text-zinc-700"}`} data-testid={`survey-tab-${v}`}>{l}</button>
          ))}
        </div>

        {tab === "overview" && results && (
          <div className="space-y-6" data-testid="survey-analytics">
            <div className="flex flex-wrap gap-3">
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="h-10 border border-zinc-300 px-3 text-sm bg-white" data-testid="survey-filter-audience">
                {TYPE_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={mmOnly} onChange={(e) => setMmOnly(e.target.checked)} data-testid="survey-filter-mm" /> Matchmaking participants only</label>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat testid="stat-sent" label="Sent" value={results.sent} />
              <Stat testid="stat-responses" label="Responses" value={results.responses} />
              <Stat testid="stat-rate" label="Response Rate" value={pct(results.response_rate)} />
              <Stat testid="stat-overall" label="Avg Overall" value={results.avg_overall ?? "—"} sub="Q1 · 1–5" />
              <Stat testid="stat-conn" label="Avg Connection Value" value={results.avg_connection_value ?? "—"} sub="Q2 · 1–5" />
              <Stat testid="stat-mm" label="Avg Matchmaking" value={results.avg_matchmaking ?? "—"} sub={`Q3 · ${results.matchmaking_rated_count} rated · ${results.matchmaking_na_count} N/A`} />
              <Stat testid="stat-potential" label="Potential Opportunity" value={pct(results.potential_opportunity_pct / 100)} sub={`${results.potential_opportunity_count} said Yes / Possibly`} />
            </div>
            <div className="card-flat p-5">
              <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-3">Q4 — Future business opportunity</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(OPP_LABELS).map(([k, l]) => (
                  <div key={k}><div className="serif text-3xl">{results.opportunity_counts[k]}</div><div className="text-xs text-zinc-500">{l} · {results.opportunity_pct[k]}%</div></div>
                ))}
              </div>
            </div>
            <div className="card-flat p-5">
              <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-3">By participant type</div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase text-zinc-400"><tr><th className="text-left pb-2">Type</th><th className="text-right pb-2">Responses</th><th className="text-right pb-2">Avg Overall</th><th className="text-right pb-2">Potential Opps</th></tr></thead>
                <tbody>
                  {results.by_type.map((b, i) => (
                    <tr key={i} className="border-t border-zinc-100"><td className="py-2">{b.type}</td><td className="py-2 text-right">{b.responses}</td><td className="py-2 text-right">{b.avg_overall ?? "—"}</td><td className="py-2 text-right">{b.potential}</td></tr>
                  ))}
                  {results.by_type.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-zinc-300">No responses yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "responses" && (
          <div className="card-flat overflow-x-auto" data-testid="survey-responses">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-[0.12em] text-zinc-500">
                <tr><th className="text-left p-3">Type</th><th className="text-left p-3">Company</th><th className="text-center p-3">Q1</th><th className="text-center p-3">Q2</th><th className="text-center p-3">Q3</th><th className="text-center p-3">Q4</th><th className="text-left p-3">Comment</th><th className="text-left p-3">Theme</th><th className="p-3"></th></tr>
              </thead>
              <tbody>
                {responses.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-100 align-top" data-testid={`survey-resp-${r.id}`}>
                    <td className="p-3 whitespace-nowrap">{r.type_label}</td>
                    <td className="p-3">{r.company}</td>
                    <td className="p-3 text-center">{r.answers.q1 ?? "—"}</td>
                    <td className="p-3 text-center">{r.answers.q2 ?? "—"}</td>
                    <td className="p-3 text-center">{r.answers.q3 === "na" ? "N/A" : (r.answers.q3 ?? "—")}</td>
                    <td className="p-3 text-center">{r.potential_opportunity ? <span className="text-emerald-700 font-medium">{r.answers.q4}</span> : (r.answers.q4 || "—")}</td>
                    <td className="p-3 max-w-[240px] text-xs text-zinc-600">{r.comment || <span className="text-zinc-300">—</span>}</td>
                    <td className="p-3">
                      <select value={r.comment_theme || ""} onChange={(e) => setTheme(r.id, e.target.value || null)} className="text-xs border border-zinc-300 px-1 py-1 bg-white" data-testid={`survey-theme-${r.id}`}>
                        <option value="">—</option>
                        {themes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="p-3"><button onClick={() => reopen(r.id)} className="text-xs underline text-zinc-500 hover:text-red-600" data-testid={`survey-reopen-${r.id}`}>Reopen</button></td>
                  </tr>
                ))}
                {responses.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-zinc-400">No responses yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
